import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  entity_name: "",
  stakeholder_type: "Warehouse Landlord / Asset Owner",
  contact: "",
  email: "",
  region: "",
  scope: "",
};

const inputClass =
  "w-full bg-night border border-line px-4 py-3 text-sm text-ink focus:border-brand outline-none font-mono transition-colors";
const labelClass = "block font-mono text-xs uppercase text-mute mb-2";

const EnquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        email: form.email.trim() || null,
      });
      toast.success("Tender enquiry registered. Our procurement desk will revert shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Submission failed. Please verify all fields and retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="bid-enquiry" className="bg-night text-ink py-24" data-testid="enquiry-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">
            Vendor & Landlord Intake
          </p>
          <h2 className="font-display text-[clamp(32px,4vw,56px)] uppercase mb-4 tracking-wide">
            Register Tender Enquiry
          </h2>
          <p className="text-sm text-mute">
            Submit requirements for RFP publishing or vendor accreditation.
          </p>
          <p className="text-xs font-mono text-mute mt-3" data-testid="enquiry-direct-lines">
            Prefer to talk first?{" "}
            <a href="tel:+919032290748" className="text-brand hover:underline" data-testid="enquiry-call-link">
              Call +91 90322 90748
            </a>{" "}
            or{" "}
            <a
              href="https://wa.me/919032290748"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:underline"
              data-testid="enquiry-whatsapp-link"
            >
              WhatsApp us
            </a>
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-surface border border-line p-8 space-y-6"
          data-testid="enquiry-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="entity-name">Organisation / Company Name</label>
              <input
                type="text"
                id="entity-name"
                required
                value={form.entity_name}
                onChange={update("entity_name")}
                className={inputClass}
                placeholder="Legal entity name"
                data-testid="entity-name-input"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="stakeholder-type">Stakeholder Classification</label>
              <select
                id="stakeholder-type"
                value={form.stakeholder_type}
                onChange={update("stakeholder_type")}
                className={inputClass}
                data-testid="stakeholder-type-select"
              >
                <option>Warehouse Landlord / Asset Owner</option>
                <option>EPC / Storage Technology Vendor</option>
                <option>3PL / Logistics Operator</option>
                <option>Turnkey Maintenance Contractor</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass} htmlFor="contact-input">Contact Coordinate</label>
              <input
                type="text"
                id="contact-input"
                required
                value={form.contact}
                onChange={update("contact")}
                className={inputClass}
                placeholder="Business phone or email"
                data-testid="contact-input"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="region-input">Target Operational Node</label>
              <input
                type="text"
                id="region-input"
                required
                value={form.region}
                onChange={update("region")}
                className={inputClass}
                placeholder="e.g. Bhiwandi, NCR, Bengaluru, Pan-India"
                data-testid="region-input"
              />
            </div>
          </div>

          <div>
            <label className={labelClass} htmlFor="email-input">Business Email (optional — for instant confirmation)</label>
            <input
              type="email"
              id="email-input"
              value={form.email}
              onChange={update("email")}
              className={inputClass}
              placeholder="you@company.com"
              data-testid="email-input"
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="brief-desc">Tender Scope Summary</label>
            <textarea
              id="brief-desc"
              rows="3"
              value={form.scope}
              onChange={update("scope")}
              className={inputClass}
              placeholder="Brief technical specifications or facility square footage..."
              data-testid="scope-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand hover:bg-brandhover disabled:opacity-60 text-night font-mono text-xs font-bold uppercase tracking-widest py-4 transition-colors"
            data-testid="enquiry-submit-button"
          >
            {submitting ? "Transmitting..." : "Submit RFP Application →"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default EnquiryForm;
